import { Component, Input } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { QrPrintOptionsDialogComponent, PrintOptions } from '../qr-print-options-dialog/qr-print-options-dialog.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-qr-generator',
    templateUrl: './qr-generator.component.html',
    styleUrls: ['./qr-generator.component.scss']
})
export class QrGeneratorComponent {
    @Input() qrData = '';
    @Input() width = 150;
    @Input() level: 'L' | 'M' | 'Q' | 'H' = 'M';
    @Input() colorDark = '#000000';
    @Input() colorLight = '#ffffff';
    @Input() allowEmptyString = false;
    @Input() margin = 4;    @Input() showActions = false;
    @Input() tableName = '';
    @Input() tableCapacity = '';
    @Input() floorName = '';

    constructor(private dialog: MatDialog, private _translate: TranslateService) { }downloadQR() {
        // Wait a bit to ensure the QR code is rendered
        setTimeout(() => {
            const qrcodeElement = document.querySelector('qrcode') as HTMLElement;
            const canvas = qrcodeElement?.querySelector('canvas') as HTMLCanvasElement;

            if (canvas) {
                const link = document.createElement('a');
                link.download = `table-${this.tableName || 'qr'}-qrcode.png`;
                link.href = canvas.toDataURL('image/png');
                link.click();
            } else {
                console.error('QR Code canvas not found for download');
            }
        }, 100);
    }    printQR() {        const dialogRef = this.dialog.open(QrPrintOptionsDialogComponent, {
            width: '500px',
            direction: this._translate.currentLang === 'ar' ? 'rtl' : 'ltr',
            data: { isMultiple: false }
        });

        dialogRef.afterClosed().subscribe((printOptions: PrintOptions) => {
            if (printOptions) {
                this.generatePrintContent(printOptions);
            }
        });
    }

    private generatePrintContent(printOptions: PrintOptions) {
        // Wait a bit to ensure the QR code is rendered
        setTimeout(() => {
            const qrcodeElement = document.querySelector('qrcode') as HTMLElement;
            const canvas = qrcodeElement?.querySelector('canvas') as HTMLCanvasElement;

            if (canvas) {
                const qrImageData = canvas.toDataURL('image/png');
                this.openPrintWindow(qrImageData, printOptions);
            } else {
                console.error('QR Code canvas not found');
            }
        }, 100);
    }

    private openPrintWindow(qrImageData: string, printOptions: PrintOptions) {
        const printWindow = window.open('', '_blank');
        if (printWindow) {
            const html = this.generatePrintHTML(qrImageData, printOptions);
            printWindow.document.write(html);
            printWindow.document.close();
        }
    }

    private generatePrintHTML(qrImageData: string, printOptions: PrintOptions): string {
        return `
            <html>
                <head>
                    <title>Table ${this.tableName} QR Code</title>
                    <meta charset="UTF-8">
                    <link rel="stylesheet" href="https://fonts.googleapis.com/icon?family=Material+Icons">
                    <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&display=swap">
                    <style>
                        * {
                            margin: 0;
                            padding: 0;
                            box-sizing: border-box;
                        }
                        body { 
                            font-family: 'Roboto', Arial, sans-serif;
                            background: white;
                            min-height: 100vh;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            padding: 20px;
                        }
                        .qr-card {
                            background: white;
                            border-radius: 20px;
                            padding: 30px;
                            text-align: center;
                            max-width: 500px;
                            width: 100%;
                            border: 3px solid #2196F3;
                            position: relative;
                        }
                        .header {
                            margin-bottom: ${printOptions.includeTitle ? '25px' : '10px'};
                            ${!printOptions.includeTitle ? 'display: none;' : ''}
                        }
                        .title {
                            font-size: 24px;
                            font-weight: 500;
                            color: #2196F3;
                            margin-bottom: 8px;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            gap: 8px;
                        }
                        .subtitle {
                            font-size: 16px;
                            color: #666;
                            font-weight: 300;
                        }
                        .table-info {
                            margin-bottom: 25px;
                            padding: 15px;
                            background: rgba(33, 150, 243, 0.1);
                            border-radius: 12px;
                            border: 2px solid rgba(33, 150, 243, 0.2);
                            ${!printOptions.includeTableInfo && !printOptions.includeFloorInfo ? 'display: none;' : ''}
                        }
                        .info-row {
                            display: flex;
                            justify-content: space-between;
                            align-items: center;
                            margin: 10px 0;
                            font-size: 16px;
                            font-weight: 600;
                        }
                        .info-label {
                            color: #2196F3;
                            display: flex;
                            align-items: center;
                            gap: 8px;
                        }
                        .info-value {
                            color: #333;
                        }
                        .table-row {
                            ${!printOptions.includeTableInfo ? 'display: none;' : ''}
                        }
                        .floor-row {
                            ${!printOptions.includeFloorInfo ? 'display: none;' : ''}
                        }
                        .qr-container {
                            margin: 20px 0;
                            padding: 15px;
                            background: white;
                            border-radius: 12px;
                            border: 3px solid #2196F3;
                            display: flex;
                            justify-content: center;
                            align-items: center;
                        }
                        .qr-image {
                            width: 600px;
                            height: 600px;
                            border-radius: 8px;
                        }
                        .scan-instruction {
                            margin-top: 20px;
                            padding: 15px;
                            background: rgba(76, 175, 80, 0.1);
                            border: 2px solid rgba(76, 175, 80, 0.3);
                            border-radius: 12px;
                            ${!printOptions.includeInstructions ? 'display: none;' : ''}
                        }
                        .scan-text {
                            font-size: 16px;
                            color: #4CAF50;
                            font-weight: 500;
                            margin-bottom: 8px;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            gap: 8px;
                        }
                        .scan-subtext {
                            font-size: 12px;
                            color: #666;
                            line-height: 1.4;
                        }
                        .material-icons {
                            font-size: inherit !important;
                        }
                        @media print {
                            body {
                                background: white !important;
                                padding: 5mm !important;
                            }
                            .qr-card {
                                border: 3px solid #2196F3 !important;
                                margin: 0 !important;
                                max-width: 100% !important;
                            }
                            @page {
                                margin: 5mm;
                                size: A4 portrait;
                            }
                        }
                    </style>
                </head>
                <body>
                    <div class="qr-card">
                        <div class="header">
                            <div class="title">
                                <span class="material-icons">qr_code_scanner</span>
                                Scan for Menu
                            </div>
                            <div class="subtitle">Point your camera at the QR code</div>
                        </div>
                        
                        <div class="table-info">
                            <div class="info-row table-row">
                                <div class="info-label">
                                    <span class="material-icons">table_restaurant</span>
                                    Table
                                </div>
                                <div class="info-value">${this.tableName}</div>
                            </div>
                            <div class="info-row table-row">
                                <div class="info-label">
                                    <span class="material-icons">people</span>
                                    Capacity
                                </div>
                                <div class="info-value">${this.tableCapacity}</div>
                            </div>
                            <div class="info-row floor-row">
                                <div class="info-label">
                                    <span class="material-icons">layers</span>
                                    Floor
                                </div>
                                <div class="info-value">${this.floorName}</div>
                            </div>
                        </div>
                        
                        <div class="qr-container">
                            <img src="${qrImageData}" alt="QR Code for Table ${this.tableName}" class="qr-image">
                        </div>
                        
                        <div class="scan-instruction">
                            <div class="scan-text">
                                <span class="material-icons">smartphone</span>
                                How to scan:
                            </div>
                            <div class="scan-subtext">Open your camera app and point it at the QR code to access the digital menu and place your order!</div>
                        </div>
                    </div>
                    
                    <script>
                        window.addEventListener('load', function() {
                            setTimeout(() => {
                                window.print();
                            }, 100);
                        });
                    </script>
                </body>
            </html>
        `;
    }
}
